import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Badge, Button, Card, Col, Container, Row, Spinner, Table,
} from 'react-bootstrap';
import axios from '../utils/api';

const GmcSyncManager = () => {
    const [status, setStatus] = useState(null);
    const [products, setProducts] = useState([]);
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [syncingId, setSyncingId] = useState('');
    const [fullSyncing, setFullSyncing] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const loadStatus = useCallback(async () => {
        try {
            setLoadingStatus(true);
            const res = await axios.get('/api/admin/merchant-sync/status');
            setStatus(res.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load GMC status');
        } finally {
            setLoadingStatus(false);
        }
    }, []);

    const loadProducts = useCallback(async () => {
        try {
            setLoadingProducts(true);
            const res = await axios.get('/api/admin/merchant-sync/products');
            setProducts(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load product sync status');
        } finally {
            setLoadingProducts(false);
        }
    }, []);

    useEffect(() => {
        loadStatus();
        loadProducts();
    }, [loadStatus, loadProducts]);

    const handleSyncProduct = async (productId, productName) => {
        setSyncingId(productId);
        setMessage('');
        setError('');
        try {
            const res = await axios.post(`/api/admin/merchant-sync/product/${productId}`);
            const synced = res.data?.synced?.length ?? 0;
            const errors = res.data?.errors?.length ?? 0;
            const isDryRun = res.data?.dry_run ? ' (dry run)' : '';
            setMessage(`"${productName}": synced ${synced} variant(s), ${errors} error(s)${isDryRun}`);
            await loadProducts();
            await loadStatus();
        } catch (err) {
            setError(`Sync failed for "${productName}": ${err.response?.data?.detail || err.message}`);
        } finally {
            setSyncingId('');
        }
    };

    const handleFullSync = async () => {
        setFullSyncing(true);
        setMessage('');
        setError('');
        try {
            const res = await axios.post('/api/admin/merchant-sync/full');
            setMessage(res.data?.message || 'Full sync started in background.');
            // Reload status after a short delay to reflect new counts
            setTimeout(async () => {
                await loadStatus();
                await loadProducts();
            }, 3000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Full sync failed');
        } finally {
            setFullSyncing(false);
        }
    };

    const syncModeBadge = (mode) => {
        if (!mode || mode === 'off') return <Badge bg="secondary">off</Badge>;
        if (mode === 'dry_run') return <Badge bg="warning" text="dark">dry_run</Badge>;
        if (mode === 'live') return <Badge bg="success">live</Badge>;
        return <Badge bg="secondary">{mode}</Badge>;
    };

    const formatDate = (val) => {
        if (!val) return '—';
        const d = new Date(val);
        if (isNaN(d.getTime())) return val;
        return new Intl.DateTimeFormat('en-IN', {
            timeZone: 'Asia/Kolkata',
            dateStyle: 'medium',
            timeStyle: 'short',
            hour12: true,
        }).format(d);
    };

    return (
        <Container className="py-4">
            <h2 className="mb-1">Google Merchant Center Sync</h2>
            <p className="text-muted mb-4">Manage product uploads to Google Merchant Center.</p>

            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
            {message && <Alert variant="success" dismissible onClose={() => setMessage('')}>{message}</Alert>}

            {/* Status card */}
            {loadingStatus ? (
                <div className="mb-4"><Spinner size="sm" /> Loading status…</div>
            ) : status && (
                <Card className="mb-4">
                    <Card.Body>
                        <Row className="g-3 align-items-center">
                            <Col xs="auto">
                                <div className="text-muted small mb-1">Sync Mode</div>
                                <div>{syncModeBadge(status.gmc_sync_mode)}</div>
                            </Col>
                            <Col xs="auto">
                                <div className="text-muted small mb-1">Merchant ID</div>
                                <div><code>{status.merchant_id}</code></div>
                            </Col>
                            <Col xs="auto">
                                <div className="text-muted small mb-1">Data Source ID</div>
                                <div><code>{status.data_source_id}</code></div>
                            </Col>
                            <Col xs="auto">
                                <div className="text-muted small mb-1">Active Products</div>
                                <div><strong>{status.active_products ?? '—'}</strong></div>
                            </Col>
                            <Col xs="auto">
                                <div className="text-muted small mb-1">Synced to GMC</div>
                                <div><strong className="text-success">{status.synced_to_gmc ?? '—'}</strong></div>
                            </Col>
                            <Col xs="auto">
                                <div className="text-muted small mb-1">Sync Errors</div>
                                <div><strong className={status.products_with_errors > 0 ? 'text-danger' : ''}>{status.products_with_errors ?? '—'}</strong></div>
                            </Col>
                            <Col xs="auto" className="ms-auto">
                                <Button
                                    variant="primary"
                                    onClick={handleFullSync}
                                    disabled={fullSyncing || status.gmc_sync_mode === 'off'}
                                    title={status.gmc_sync_mode === 'off' ? 'Set GMC_SYNC_MODE=dry_run or live to enable' : ''}
                                >
                                    {fullSyncing ? <><Spinner size="sm" className="me-1" />Syncing…</> : '⬆ Sync All Active Products'}
                                </Button>
                            </Col>
                        </Row>
                        {status.gmc_sync_mode === 'off' && (
                            <Alert variant="warning" className="mt-3 mb-0 py-2">
                                GMC sync is <strong>off</strong>. Set <code>GMC_SYNC_MODE=dry_run</code> or <code>live</code> in your environment to enable.
                            </Alert>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* Products table */}
            <h5 className="mb-3">Product Sync Status</h5>
            {loadingProducts ? (
                <div><Spinner size="sm" /> Loading products…</div>
            ) : (
                <Table responsive hover size="sm">
                    <thead className="table-dark">
                        <tr>
                            <th>Product</th>
                            <th>SKU</th>
                            <th>Active</th>
                            <th>GMC Status</th>
                            <th>Last Synced</th>
                            <th>Error</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.length === 0 && (
                            <tr><td colSpan={7} className="text-center text-muted py-4">No products found.</td></tr>
                        )}
                        {products.map((p) => (
                            <tr key={p.id}>
                                <td>{p.name}</td>
                                <td><code className="small">{p.source_sku}</code></td>
                                <td>
                                    {p.is_active
                                        ? <Badge bg="success">Active</Badge>
                                        : <Badge bg="secondary">Inactive</Badge>}
                                </td>
                                <td>
                                    {p.gmc_product_id
                                        ? <Badge bg="success">Synced</Badge>
                                        : <Badge bg="light" text="dark">Not synced</Badge>}
                                </td>
                                <td className="small text-muted">{formatDate(p.gmc_last_synced_at)}</td>
                                <td>
                                    {p.gmc_sync_error
                                        ? <span className="text-danger small" title={p.gmc_sync_error}>⚠ Error</span>
                                        : ''}
                                </td>
                                <td>
                                    <Button
                                        size="sm"
                                        variant="outline-primary"
                                        disabled={!!syncingId || !p.is_active || status?.gmc_sync_mode === 'off'}
                                        onClick={() => handleSyncProduct(p.id, p.name)}
                                        title={!p.is_active ? 'Product must be active to sync' : ''}
                                    >
                                        {syncingId === p.id
                                            ? <Spinner size="sm" />
                                            : '↑ Sync'}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </Container>
    );
};

export default GmcSyncManager;
